import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  TextInput,
  Animated,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@constants/colors';
import { Button } from '@components/common/Button';
import { joinGroupByInviteCode, getGroupByInviteCode } from '@services/groups/groupService';

interface JoinGroupModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: (groupId: string) => void;
}

export const JoinGroupModal: React.FC<JoinGroupModalProps> = ({ visible, onClose, onSuccess }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [groupPreview, setGroupPreview] = useState<{ name: string; memberCount: number } | null>(
    null
  );

  const inputRef = useRef<TextInput>(null);
  const previewAnim = useRef(new Animated.Value(0)).current;

  // Auto-focus input when modal opens
  useEffect(() => {
    if (visible) {
      setTimeout(() => inputRef.current?.focus(), 300);
    } else {
      // Reset on close
      setInviteCode('');
      setError('');
      setGroupPreview(null);
      setValidating(false);
    }
  }, [visible]);

  // Animate preview appearance
  useEffect(() => {
    if (groupPreview) {
      Animated.spring(previewAnim, {
        toValue: 1,
        useNativeDriver: true,
        tension: 100,
        friction: 10,
      }).start();
    } else {
      previewAnim.setValue(0);
    }
  }, [groupPreview]);

  const handleCodeChange = async (text: string) => {
    const code = text.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setInviteCode(code);
    setError('');
    setGroupPreview(null);

    // Preview group when code is 6 characters
    if (code.length === 6) {
      setValidating(true);
      const { data, error } = await getGroupByInviteCode(code);
      setValidating(false);

      if (data) {
        setGroupPreview({ name: data.name, memberCount: data.member_count || 0 });
      } else {
        setError(error || 'Invalid invite code');
      }
    }
  };

  const handleJoin = async () => {
    if (inviteCode.length !== 6) {
      setError('Please enter a valid 6-character code');
      return;
    }

    setLoading(true);
    setError('');

    const { success, groupId, error: joinError } = await joinGroupByInviteCode(inviteCode);

    setLoading(false);

    if (success && groupId) {
      onSuccess(groupId);
      onClose();
    } else {
      setError(joinError || 'Failed to join group');
    }
  };

  const handleClose = () => {
    setInviteCode('');
    setError('');
    setGroupPreview(null);
    onClose();
  };

  // Render individual character boxes
  const renderCharacterBoxes = () => {
    const boxes = [];
    for (let i = 0; i < 6; i++) {
      const char = inviteCode[i] || '';
      const isFilled = char !== '';
      const isCurrent = i === inviteCode.length;

      boxes.push(
        <View
          key={i}
          style={[
            styles.charBox,
            isFilled && styles.charBoxFilled,
            isCurrent && styles.charBoxActive,
          ]}
        >
          <Text style={[styles.charText, isFilled && styles.charTextFilled]}>{char}</Text>
        </View>
      );
    }
    return boxes;
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
      >
        <Pressable style={styles.overlay} onPress={handleClose}>
          <View style={styles.modal}>
            {/* Header */}
            <View style={styles.header}>
              <View style={styles.headerLeft}>
                <View style={styles.iconCircle}>
                  <Ionicons name="people" size={24} color={Colors.accent.primary} />
                </View>
                <Text style={styles.title}>Join Group</Text>
              </View>
              <Pressable onPress={handleClose} style={styles.closeButton} hitSlop={8}>
                <Ionicons name="close" size={24} color={Colors.text.secondary} />
              </Pressable>
            </View>

            {/* Content */}
            <View style={styles.content}>
              <Text style={styles.description}>
                Enter the 6-character invite code shared by the group admin
              </Text>

              {/* Hidden Input (for keyboard) */}
              <TextInput
                ref={inputRef}
                style={styles.hiddenInput}
                value={inviteCode}
                onChangeText={handleCodeChange}
                autoCapitalize="characters"
                keyboardType="default"
                maxLength={6}
                editable={!loading && !validating}
                autoComplete="off"
                autoCorrect={false}
                returnKeyType="done"
                onSubmitEditing={handleJoin}
              />

              {/* Character Boxes */}
              <Pressable onPress={() => inputRef.current?.focus()} style={styles.charBoxContainer}>
                {renderCharacterBoxes()}
              </Pressable>

              {/* Progress Indicator */}
              <Text style={styles.progress}>{String(inviteCode.length)}/6 characters</Text>

              {/* Validating State */}
              {validating && (
                <View style={styles.validatingContainer}>
                  <ActivityIndicator size="small" color={Colors.accent.primary} />
                  <Text style={styles.validatingText}>Checking code...</Text>
                </View>
              )}

              {/* Error */}
              {error && (
                <View style={styles.errorContainer}>
                  <Ionicons name="alert-circle" size={16} color={Colors.status.error} />
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              )}

              {/* Group Preview with Animation */}
              {groupPreview && !error && (
                <Animated.View
                  style={[
                    styles.previewContainer,
                    {
                      opacity: previewAnim,
                      transform: [
                        {
                          translateY: previewAnim.interpolate({
                            inputRange: [0, 1],
                            outputRange: [20, 0],
                          }),
                        },
                      ],
                    },
                  ]}
                >
                  <View style={styles.previewHeader}>
                    <Ionicons name="checkmark-circle" size={20} color={Colors.status.success} />
                    <Text style={styles.previewLabel}>Group Found</Text>
                  </View>
                  <View style={styles.preview}>
                    <View style={styles.previewIcon}>
                      <Ionicons name="people" size={24} color={Colors.accent.primary} />
                    </View>
                    <View style={styles.previewText}>
                      <Text style={styles.previewName}>{groupPreview.name}</Text>
                      <Text style={styles.previewMembers}>
                        {groupPreview.memberCount}{' '}
                        {groupPreview.memberCount === 1 ? 'member' : 'members'}
                      </Text>
                    </View>
                  </View>
                </Animated.View>
              )}
            </View>

            {/* Footer */}
            <View style={styles.footer}>
              <Button
                title={loading ? 'Joining...' : 'Join Group'}
                onPress={handleJoin}
                variant="primary"
                disabled={loading || validating || inviteCode.length !== 6 || !groupPreview}
                loading={loading}
                fullWidth
              />
              <Pressable onPress={handleClose} style={styles.cancelButton} disabled={loading}>
                <Text style={styles.cancelText}>Cancel</Text>
              </Pressable>
            </View>
          </View>
        </Pressable>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modal: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 20,
    width: '100%',
    maxWidth: 420,
    borderWidth: 1,
    borderColor: Colors.border.default,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.5,
    shadowRadius: 20,
    elevation: 10,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: `${Colors.accent.primary}20`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  closeButton: {
    padding: 8,
    borderRadius: 8,
  },
  content: {
    padding: 24,
  },
  description: {
    fontSize: 14,
    color: Colors.text.secondary,
    marginBottom: 24,
    lineHeight: 20,
    textAlign: 'center',
  },
  hiddenInput: {
    position: 'absolute',
    opacity: 0,
    height: 0,
    width: 0,
  },
  charBoxContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 12,
  },
  charBox: {
    width: 48,
    height: 60,
    backgroundColor: Colors.background.tertiary,
    borderWidth: 2,
    borderColor: Colors.border.default,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  charBoxActive: {
    borderColor: Colors.accent.primary,
    backgroundColor: `${Colors.accent.primary}10`,
  },
  charBoxFilled: {
    borderColor: Colors.accent.primary,
    backgroundColor: Colors.background.tertiary,
  },
  charText: {
    fontSize: 28,
    fontWeight: '700',
    color: Colors.text.tertiary,
  },
  charTextFilled: {
    color: Colors.text.primary,
  },
  progress: {
    fontSize: 12,
    color: Colors.text.tertiary,
    textAlign: 'center',
    marginBottom: 16,
  },
  validatingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
  },
  validatingText: {
    fontSize: 14,
    color: Colors.accent.primary,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: `${Colors.status.error}15`,
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: Colors.status.error,
  },
  errorText: {
    flex: 1,
    color: Colors.status.error,
    fontSize: 14,
  },
  previewContainer: {
    marginTop: 20,
  },
  previewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  previewLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.status.success,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  preview: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.background.tertiary,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.border.default,
  },
  previewIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: `${Colors.accent.primary}20`,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  previewText: {
    flex: 1,
  },
  previewName: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  previewMembers: {
    fontSize: 14,
    color: Colors.text.secondary,
  },
  footer: {
    padding: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
  },
  cancelButton: {
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  cancelText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.secondary,
  },
});
